import { useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  useDeleteDashboard,
  useCreateDashboard,
  useUpdateDashboard,
} from "@/api/dashboard";
import { useDeleteTracker, useUpdateStatusTracker, useRunTracker } from "@/api/tracker";
import { useDeleteEnrichment, useRunEnrichment } from "@/api/enrichment";
import { useDeleteAlert, useRunAlert, useUpdateAlert } from "@/api/alert";
import { useDeleteWidget } from "@/api/widget";
import { useDeleteInvestigation } from "@/api/investigation";
import { useSidebarContext } from "@/hooks/use-sidebar";
import { useDashboards } from "@/api/dashboard";
import useNotif from "@/hooks/use-notif";
import useTranslation from "@/hooks/use-translation";
import logger from "@/utils/logger";
import { optimisticListMutation, urlPredicate } from "@/utils/optimistic";

/**
 * Hook that provides bulk operations for multi-selected items.
 * Each operation returns a function that takes an array of IDs.
 */
export function useMultiSelectOperations() {
  const queryClient = useQueryClient();
  const { addSuccess, addError } = useNotif();
  const { t } = useTranslation();
  const { pinItem, changeDashboardColor } = useSidebarContext();
  const { data: dashboards = [] } = useDashboards();

  // Mutations
  const deleteDashboardMutation = useDeleteDashboard();
  const createDashboardMutation = useCreateDashboard();
  const updateDashboardMutation = useUpdateDashboard();
  
  const deleteTrackerMutation = useDeleteTracker();
  const updateTrackerStatusMutation = useUpdateStatusTracker();
  const runTrackerMutation = useRunTracker();
  
  const deleteEnrichmentMutation = useDeleteEnrichment();
  const runEnrichmentMutation = useRunEnrichment();
  
  const deleteAlertMutation = useDeleteAlert();
  const updateAlertMutation = useUpdateAlert();
  const runAlertMutation = useRunAlert();

  const deleteWidgetMutation = useDeleteWidget();
  const deleteInvestigationMutation = useDeleteInvestigation();

  // Helper to invalidate queries
  const invalidateQueries = useCallback(
    (urlPart: string) => {
      queryClient.invalidateQueries({
        predicate: (query) => {
          const queryKey = query.queryKey[0];
          if (typeof queryKey === "string") {
            try {
              const config = JSON.parse(queryKey);
              return config.url?.includes(urlPart);
            } catch {
              return false;
            }
          }
          return false;
        },
      });
    },
    [queryClient]
  );

  // ============================================================================
  // Dashboard Operations
  // ============================================================================

  const deleteDashboards = useCallback(
    async (ids: string[]) => {
      try {
        await optimisticListMutation({
          queryClient,
          queryPredicate: urlPredicate("dashboard"),
          operation: { type: "remove_many", ids },
          mutationFn: async () => {
            const results = await Promise.allSettled(
              ids.map((id) => deleteDashboardMutation.mutateAsync({ uriParams: { id } })),
            );
            const failed = results.filter((r) => r.status === "rejected");
            if (failed.length) throw new Error(`${failed.length} failed`);
          },
          onSettled: () => invalidateQueries("dashboard"),
        });
        addSuccess(t("n_dashboards_deleted", { count: ids.length }));
      } catch {
        addError(t("n_dashboards_delete_failed", { count: ids.length }));
      }
    },
    [queryClient, deleteDashboardMutation, invalidateQueries, addSuccess, addError],
  );

  const duplicateDashboards = useCallback(
    async (ids: string[]) => {
      const errors: string[] = [];
      
      for (const id of ids) {
        const dashboard = dashboards.find((d) => d.id === id);
        if (!dashboard) {
          errors.push(id);
          continue;
        }

        try {
          // Note: Widgets are now separate documents referencing dashboards.
          // Duplicating a dashboard creates a new empty dashboard.
          // To duplicate widgets, they would need to be fetched and recreated separately.
          await createDashboardMutation.mutateAsync({
            name: `${dashboard.name || "Dashboard"} (copy)`,
            description: dashboard.description || undefined,
            filters: dashboard.filter || undefined,
          });
        } catch (error) {
          logger.error("Failed to duplicate dashboard:", { id, error });
          errors.push(id);
        }
      }

      invalidateQueries("dashboard");

      if (errors.length > 0) {
        addError(t("n_dashboards_duplicate_failed", { count: errors.length }));
      } else {
        addSuccess(t("n_dashboards_duplicated", { count: ids.length }));
      }
    },
    [dashboards, createDashboardMutation, invalidateQueries, addSuccess, addError]
  );

  const pinDashboards = useCallback(
    async (ids: string[], pinned: boolean) => {
      for (const id of ids) {
        await pinItem(id, "dashboard", pinned);
      }
      addSuccess(t(pinned ? "n_dashboards_pinned" : "n_dashboards_unpinned", { count: ids.length }));
    },
    [pinItem, addSuccess]
  );

  const changeDashboardsColor = useCallback(
    async (ids: string[], color: string) => {
      const errors: string[] = [];

      for (const id of ids) {
        try {
          await changeDashboardColor(id, color);
        } catch (error) {
          logger.error("Failed to change dashboard color:", { id, error });
          errors.push(id);
        }
      }

      if (errors.length > 0) {
        addError(t("n_dashboards_color_change_failed", { count: errors.length }));
      } else {
        addSuccess(t("n_dashboards_color_changed", { count: ids.length }));
      }
    },
    [changeDashboardColor, addSuccess, addError]
  );

  // ============================================================================
  // Tracker Operations
  // ============================================================================

  const deleteTrackers = useCallback(
    async (ids: string[]) => {
      try {
        await optimisticListMutation({
          queryClient,
          queryPredicate: urlPredicate("tracker"),
          operation: { type: "remove_many", ids },
          mutationFn: async () => {
            const results = await Promise.allSettled(
              ids.map((id) => deleteTrackerMutation.mutateAsync({ uriParams: { id } })),
            );
            const failed = results.filter((r) => r.status === "rejected");
            if (failed.length) throw new Error(`${failed.length} failed`);
          },
          onSettled: () => invalidateQueries("tracker"),
        });
        addSuccess(t("n_trackers_deleted", { count: ids.length }));
      } catch {
        addError(t("n_trackers_delete_failed", { count: ids.length }));
      }
    },
    [queryClient, deleteTrackerMutation, invalidateQueries, addSuccess, addError],
  );

  const activateTrackers = useCallback(
    async (ids: string[]) => {
      try {
        await optimisticListMutation({
          queryClient,
          queryPredicate: urlPredicate("tracker"),
          operation: { type: "update_many", ids, changes: { status: "active" } as Record<string, unknown> },
          mutationFn: async () => {
            const results = await Promise.allSettled(
              ids.map((id) =>
                updateTrackerStatusMutation.mutateAsync({
                  uriParams: { id, tracker: { id } as never },
                  status: "active",
                }),
              ),
            );
            const failed = results.filter((r) => r.status === "rejected");
            if (failed.length) throw new Error(`${failed.length} failed`);
          },
          onSettled: () => invalidateQueries("tracker"),
        });
        addSuccess(t("n_trackers_activated", { count: ids.length }));
      } catch {
        addError(t("n_trackers_activate_failed", { count: ids.length }));
      }
    },
    [queryClient, updateTrackerStatusMutation, invalidateQueries, addSuccess, addError],
  );

  const deactivateTrackers = useCallback(
    async (ids: string[]) => {
      try {
        await optimisticListMutation({
          queryClient,
          queryPredicate: urlPredicate("tracker"),
          operation: { type: "update_many", ids, changes: { status: "inactive" } as Record<string, unknown> },
          mutationFn: async () => {
            const results = await Promise.allSettled(
              ids.map((id) =>
                updateTrackerStatusMutation.mutateAsync({
                  uriParams: { id, tracker: { id } as never },
                  status: "inactive",
                }),
              ),
            );
            const failed = results.filter((r) => r.status === "rejected");
            if (failed.length) throw new Error(`${failed.length} failed`);
          },
          onSettled: () => invalidateQueries("tracker"),
        });
        addSuccess(t("n_trackers_deactivated", { count: ids.length }));
      } catch {
        addError(t("n_trackers_deactivate_failed", { count: ids.length }));
      }
    },
    [queryClient, updateTrackerStatusMutation, invalidateQueries, addSuccess, addError],
  );

  const runTrackers = useCallback(
    async (ids: string[]) => {
      const errors: string[] = [];
      
      for (const id of ids) {
        try {
          await runTrackerMutation.mutateAsync({
            uriParams: { tracker_id: id },
          });
        } catch (error) {
          logger.error("Failed to run tracker:", { id, error });
          errors.push(id);
        }
      }

      if (errors.length > 0) {
        addError(t("n_trackers_execute_failed", { count: errors.length }));
      } else {
        addSuccess(t("n_trackers_executed", { count: ids.length }));
      }
    },
    [runTrackerMutation, addSuccess, addError]
  );

  // ============================================================================
  // Enrichment Operations
  // ============================================================================

  const deleteEnrichments = useCallback(
    async (ids: string[]) => {
      try {
        await optimisticListMutation({
          queryClient,
          queryPredicate: urlPredicate("enrichment"),
          operation: { type: "remove_many", ids },
          mutationFn: async () => {
            const results = await Promise.allSettled(
              ids.map((id) => deleteEnrichmentMutation.mutateAsync({ uriParams: { id } })),
            );
            const failed = results.filter((r) => r.status === "rejected");
            if (failed.length) throw new Error(`${failed.length} failed`);
          },
          onSettled: () => invalidateQueries("enrichment"),
        });
        addSuccess(t("n_enrichments_deleted", { count: ids.length }));
      } catch {
        addError(t("n_enrichments_delete_failed", { count: ids.length }));
      }
    },
    [queryClient, deleteEnrichmentMutation, invalidateQueries, addSuccess, addError],
  );

  const activateEnrichments = useCallback(
    async (ids: string[]) => {
      // Note: Activate/deactivate not supported by API yet
      logger.warn("Enrichment activate not implemented in API", { ids });
      addError("Activation d'enrichissement non supportée");
    },
    [addError]
  );

  const deactivateEnrichments = useCallback(
    async (ids: string[]) => {
      // Note: Activate/deactivate not supported by API yet
      logger.warn("Enrichment deactivate not implemented in API", { ids });
      addError("Désactivation d'enrichissement non supportée");
    },
    [addError]
  );

  const runEnrichments = useCallback(
    async (ids: string[]) => {
      const errors: string[] = [];
      
      for (const id of ids) {
        try {
          await runEnrichmentMutation.mutateAsync({
            uriParams: { enrichment_id: id },
          });
        } catch (error) {
          logger.error("Failed to run enrichment:", { id, error });
          errors.push(id);
        }
      }

      if (errors.length > 0) {
        addError(t("n_enrichments_execute_failed", { count: errors.length }));
      } else {
        addSuccess(t("n_enrichments_executed", { count: ids.length }));
      }
    },
    [runEnrichmentMutation, addSuccess, addError]
  );

  // ============================================================================
  // Alert Operations
  // ============================================================================

  const deleteAlerts = useCallback(
    async (ids: string[]) => {
      try {
        await optimisticListMutation({
          queryClient,
          queryPredicate: urlPredicate("alert"),
          operation: { type: "remove_many", ids },
          mutationFn: async () => {
            const results = await Promise.allSettled(
              ids.map((id) =>
                deleteAlertMutation.mutateAsync({
                  uriParams: { id },
                } as Record<string, never> & { uriParams: { id: string } }),
              ),
            );
            const failed = results.filter((r) => r.status === "rejected");
            if (failed.length) throw new Error(`${failed.length} failed`);
          },
          onSettled: () => invalidateQueries("alert"),
        });
        addSuccess(t("n_alerts_deleted", { count: ids.length }));
      } catch {
        addError(t("n_alerts_delete_failed", { count: ids.length }));
      }
    },
    [queryClient, deleteAlertMutation, invalidateQueries, addSuccess, addError],
  );

  const activateAlerts = useCallback(
    async (ids: string[]) => {
      try {
        await optimisticListMutation({
          queryClient,
          queryPredicate: urlPredicate("alert"),
          operation: { type: "update_many", ids, changes: { enabled: true } as Record<string, unknown> },
          mutationFn: async () => {
            const results = await Promise.allSettled(
              ids.map((id) =>
                updateAlertMutation.mutateAsync({ uriParams: { id }, enabled: true }),
              ),
            );
            const failed = results.filter((r) => r.status === "rejected");
            if (failed.length) throw new Error(`${failed.length} failed`);
          },
          onSettled: () => invalidateQueries("alert"),
        });
        addSuccess(t("n_alerts_activated", { count: ids.length }));
      } catch {
        addError(t("n_alerts_activate_failed", { count: ids.length }));
      }
    },
    [queryClient, updateAlertMutation, invalidateQueries, addSuccess, addError],
  );

  const deactivateAlerts = useCallback(
    async (ids: string[]) => {
      try {
        await optimisticListMutation({
          queryClient,
          queryPredicate: urlPredicate("alert"),
          operation: { type: "update_many", ids, changes: { enabled: false } as Record<string, unknown> },
          mutationFn: async () => {
            const results = await Promise.allSettled(
              ids.map((id) =>
                updateAlertMutation.mutateAsync({ uriParams: { id }, enabled: false }),
              ),
            );
            const failed = results.filter((r) => r.status === "rejected");
            if (failed.length) throw new Error(`${failed.length} failed`);
          },
          onSettled: () => invalidateQueries("alert"),
        });
        addSuccess(t("n_alerts_deactivated", { count: ids.length }));
      } catch {
        addError(t("n_alerts_deactivate_failed", { count: ids.length }));
      }
    },
    [queryClient, updateAlertMutation, invalidateQueries, addSuccess, addError],
  );

  const runAlerts = useCallback(
    async (ids: string[]) => {
      const errors: string[] = [];
      
      for (const id of ids) {
        try {
          await runAlertMutation.mutateAsync({
            uriParams: { id },
          } as Record<string, never> & { uriParams: { id: string } });
        } catch (error) {
          logger.error("Failed to run alert:", { id, error });
          errors.push(id);
        }
      }

      if (errors.length > 0) {
        addError(t("n_alerts_execute_failed", { count: errors.length }));
      } else {
        addSuccess(t("n_alerts_executed", { count: ids.length }));
      }
    },
    [runAlertMutation, addSuccess, addError]
  );

  // ============================================================================
  // Widget Operations
  // ============================================================================

  const deleteWidgets = useCallback(
    async (ids: string[]) => {
      try {
        await optimisticListMutation({
          queryClient,
          queryPredicate: urlPredicate("widget"),
          operation: { type: "remove_many", ids },
          mutationFn: async () => {
            const results = await Promise.allSettled(
              ids.map((id) => deleteWidgetMutation.mutateAsync({ uriParams: { id } })),
            );
            const failed = results.filter((r) => r.status === "rejected");
            if (failed.length) throw new Error(`${failed.length} failed`);
          },
          onSettled: () => invalidateQueries("widget"),
        });
        addSuccess(t("n_widgets_deleted", { count: ids.length }));
      } catch {
        addError(t("n_widgets_delete_failed", { count: ids.length }));
      }
    },
    [queryClient, deleteWidgetMutation, invalidateQueries, addSuccess, addError],
  );

  // ============================================================================
  // Investigation Operations
  // ============================================================================

  const deleteInvestigations = useCallback(
    async (ids: string[]) => {
      try {
        await optimisticListMutation({
          queryClient,
          queryPredicate: urlPredicate("investigation"),
          operation: { type: "remove_many", ids },
          mutationFn: async () => {
            const results = await Promise.allSettled(
              ids.map((id) => deleteInvestigationMutation.mutateAsync({ uriParams: { id } })),
            );
            const failed = results.filter((r) => r.status === "rejected");
            if (failed.length) throw new Error(`${failed.length} failed`);
          },
          onSettled: () => invalidateQueries("investigation"),
        });
        addSuccess(t("n_investigations_deleted", { count: ids.length }));
      } catch {
        addError(t("n_investigations_delete_failed", { count: ids.length }));
      }
    },
    [queryClient, deleteInvestigationMutation, invalidateQueries, addSuccess, addError],
  );

  return {
    // Dashboard operations
    deleteDashboards,
    duplicateDashboards,
    pinDashboards,
    changeDashboardsColor,

    // Tracker operations
    deleteTrackers,
    activateTrackers,
    deactivateTrackers,
    runTrackers,

    // Enrichment operations
    deleteEnrichments,
    activateEnrichments,
    deactivateEnrichments,
    runEnrichments,

    // Alert operations
    deleteAlerts,
    activateAlerts,
    deactivateAlerts,
    runAlerts,

    // Widget operations
    deleteWidgets,

    // Investigation operations
    deleteInvestigations,
  };
}
